/**
 * Whisky Library PostHog helper — safe to call before posthog.init() completes.
 * All theme and funnel scripts should use window.whiskyPosthog.*
 */
(function (window) {
  'use strict';

  var queue = [];
  var sdkReady = false;

  function getPosthog() {
    return typeof window.posthog !== 'undefined' ? window.posthog : null;
  }

  function isSdkReady() {
    var ph = getPosthog();
    return !!(ph && ph.__loaded);
  }

  function run(method, args) {
    var ph = getPosthog();
    if (!ph || typeof ph[method] !== 'function') return false;
    try {
      ph[method].apply(ph, args);
      return true;
    } catch (err) {
      return false;
    }
  }

  function enqueue(method, args) {
    queue.push({ method: method, args: args });
  }

  function flush() {
    if (!queue.length) return;
    var pending = queue.slice();
    queue.length = 0;
    pending.forEach(function (item) {
      run(item.method, item.args);
    });
  }

  function call(method, args) {
    if (isSdkReady()) {
      run(method, args);
      return;
    }
    enqueue(method, args);
  }

  function markSdkReady() {
    if (sdkReady) return;
    sdkReady = true;
    flush();
    // Re-dispatch here too: analytics-posthog.liquid's `loaded()` callback fires this same
    // event unconditionally, even if this script (loaded with `defer`) hasn't executed yet —
    // on a page where PostHog's own async init finishes before this deferred script runs,
    // that first dispatch fires into the void (window.whiskyPosthog doesn't exist yet, so
    // nothing is listening). Whichever of the two finishes last needs to be the one that
    // actually notifies waiting listeners; markSdkReady()'s one-time guard above makes this
    // safe to call unconditionally without double-firing.
    try {
      window.dispatchEvent(new CustomEvent('whisky-posthog-ready'));
    } catch (e) {
      // ignore
    }
    if (isDebug()) {
      console.info('[whiskyPosthog] SDK ready, queue flushed');
    }
  }

  function isDebug() {
    try {
      return new URLSearchParams(window.location.search).get('ph_debug') === '1';
    } catch (e) {
      return false;
    }
  }

  function capture(event, properties) {
    if (!event) return;
    var props = properties || {};
    props.page_path = props.page_path || window.location.pathname;
    props.page_url = props.page_url || window.location.href;
    if (isDebug()) {
      console.info('[whiskyPosthog] capture', event, props);
    }
    call('capture', [event, props]);
  }

  function register(properties) {
    if (!properties) return;
    call('register', [properties]);
  }

  function registerOnce(properties) {
    if (!properties) return;
    call('register_once', [properties]);
  }

  function identify(distinctId, properties) {
    if (!distinctId) return;
    call('identify', [distinctId, properties || {}]);
  }

  function setPersonProperties(properties, setOnceProperties) {
    var setProps = properties && typeof properties === 'object' ? properties : null;
    var onceProps =
      setOnceProperties && typeof setOnceProperties === 'object' ? setOnceProperties : null;
    if (!setProps && !onceProps) return;
    call('setPersonProperties', [setProps || {}, onceProps || {}]);
  }

  function setPersonPropertiesForFlags(properties, reloadFeatureFlags) {
    if (!properties) return;
    if (typeof reloadFeatureFlags === 'boolean') {
      call('setPersonPropertiesForFlags', [properties, reloadFeatureFlags]);
      return;
    }
    call('setPersonPropertiesForFlags', [properties]);
  }

  var QUIZ_SESSION_ID_KEY = 'quiz_sessionId';
  var MILESTONE_USER_STORAGE_KEY = 'ph_milestone_user';
  /** Person property for 3-month-milestone release conditions (boolean). */
  var MILESTONE_USER_PROP = 'milestone-user';

  function hasQuizSessionId() {
    try {
      return !!(window.localStorage.getItem(QUIZ_SESSION_ID_KEY) || '').trim();
    } catch (e) {
      return false;
    }
  }

  function getStoredMilestoneUser() {
    try {
      var raw = window.localStorage.getItem(MILESTONE_USER_STORAGE_KEY);
      if (raw === 'true') return true;
      if (raw === 'false') return false;
    } catch (e) {
      // ignore
    }
    return null;
  }

  /**
   * Decide once: milestone_user = true only if quiz_sessionId did not exist yet.
   * Returning quiz-takers (session already in localStorage) get false.
   * @returns {boolean}
   */
  function getOrCreateMilestoneUser() {
    var stored = getStoredMilestoneUser();
    if (stored !== null) return stored;

    // No prior quiz session → new milestone-experiment user.
    var isMilestoneUser = !hasQuizSessionId();
    try {
      window.localStorage.setItem(
        MILESTONE_USER_STORAGE_KEY,
        isMilestoneUser ? 'true' : 'false'
      );
    } catch (e) {
      // ignore storage errors
    }
    return isMilestoneUser;
  }

  /**
   * Record milestone_user once on the person (quiz landing) + for flag evaluation.
   * @returns {boolean}
   */
  function setMilestoneUserOnce() {
    var isMilestoneUser = getOrCreateMilestoneUser();
    var props = {};
    props[MILESTONE_USER_PROP] = isMilestoneUser;
    setPersonProperties({}, props);
    setPersonPropertiesForFlags(props, false);
    if (isDebug()) {
      console.info('[whiskyPosthog] set_once milestone_user', isMilestoneUser, {
        had_quiz_session: hasQuizSessionId()
      });
    }
    return isMilestoneUser;
  }

  /**
   * Re-send stored milestone_user for feature-flag /decide (taste profile).
   * Caller must reloadFeatureFlags() and read the variant inside onFeatureFlags.
   * @returns {boolean|null}
   */
  function applyMilestoneUserForFlags() {
    var stored = getStoredMilestoneUser();
    if (stored === null) return null;
    var props = {};
    props[MILESTONE_USER_PROP] = stored;
    setPersonProperties({}, props);
    setPersonPropertiesForFlags(props, false);
    if (isDebug()) {
      console.info('[whiskyPosthog] apply milestone_user for flags', stored);
    }
    return stored;
  }

  // Back-compat aliases (older call sites / cached bundles).
  function setFirstSeenAtOnce() {
    return setMilestoneUserOnce();
  }

  function applyFirstSeenAtForFlags() {
    return applyMilestoneUserForFlags();
  }

  function isFeatureEnabled(flagKey) {
    if (!flagKey) return false;
    var ph = getPosthog();
    if (!ph || typeof ph.isFeatureEnabled !== 'function') return false;
    try {
      return !!ph.isFeatureEnabled(flagKey);
    } catch (err) {
      return false;
    }
  }

  function getFeatureFlag(flagKey) {
    if (!flagKey) return undefined;
    var ph = getPosthog();
    if (!ph || typeof ph.getFeatureFlag !== 'function') return undefined;
    try {
      return ph.getFeatureFlag(flagKey);
    } catch (err) {
      return undefined;
    }
  }

  function onFeatureFlags(callback) {
    if (typeof callback !== 'function') return;

    function register() {
      var ph = getPosthog();
      if (!ph || typeof ph.onFeatureFlags !== 'function') {
        return;
      }
      try {
        ph.onFeatureFlags(callback);
      } catch (err) {
        callback();
      }
    }

    if (isSdkReady()) {
      register();
      return;
    }

    window.addEventListener('whisky-posthog-ready', register, { once: true });
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (isSdkReady()) {
        clearInterval(timer);
        register();
      } else if (attempts >= 120) {
        clearInterval(timer);
        callback();
      }
    }, 100);
  }

  function reloadFeatureFlags() {
    call('reloadFeatureFlags', []);
  }

  /** PostHog flag key — multivariate `test` (or boolean true) enables 3-month-milestone UI. */
  var FEATURE_FLAG_3_MONTH_MILESTONE = '3-month-milestone';

  function is3MonthMilestoneEnabled() {
    // Milestone is the default quiz-landing / taste-profile offer (no PostHog gate).
    return true;
  }

  /**
   * Multivariate variant only. `false`/`undefined` mean unmatched or not loaded yet —
   * NOT the control arm. Caching those as control caused sticky old-hero bugs.
   * @returns {'test'|'control'|null}
   */
  function readMilestoneVariantFromFlag() {
    var flag = getFeatureFlag(FEATURE_FLAG_3_MONTH_MILESTONE);
    if (flag === 'test' || flag === true) return 'test';
    if (flag === 'control') return 'control';
    return null;
  }

  /** True only when PostHog returned an explicit multivariate variant key. */
  function isMilestoneFlagResolved() {
    return readMilestoneVariantFromFlag() !== null;
  }

  /**
   * Apply milestone_user, reload flags, then check variant only inside onFeatureFlags.
   * Avoids the race where the flag is read from cache before milestone_user is in /decide.
   * control / unmatched → existing UI; test → 3-month-milestone UI.
   * Shared by the quiz-hero section and the taste-profile results page so both read the
   * same flag the same way.
   *
   * The returned promise is bounded by `timeoutMs` so a render decision is never blocked
   * indefinitely. But a slow /decide can still resolve genuinely *after* that timeout fires —
   * `onResolved`, if given, is called every time a real (non-null) value is observed,
   * independent of whether the promise itself already settled, so a caller that wants to
   * durably remember the answer (e.g. cache it) doesn't lose a late-arriving correct value
   * just because the bounded promise already gave up and resolved to the timeout fallback.
   * @param {number} [timeoutMs]
   * @param {(enabled: boolean) => void} [onResolved]
   * @returns {Promise<boolean>}
   */
  function waitFor3MonthMilestoneFlag(timeoutMs, onResolved) {
    // Live test always wins over a stale control cache from an earlier race.
    if (is3MonthMilestoneEnabled()) {
      if (typeof onResolved === 'function') onResolved(true);
      return Promise.resolve(true);
    }

    var cachedVariant = getCachedMilestoneVariant();
    if (cachedVariant) {
      var cachedEnabled = cachedVariant === 'test';
      if (typeof onResolved === 'function') onResolved(cachedEnabled);
      return Promise.resolve(cachedEnabled);
    }

    var wait = typeof timeoutMs === 'number' ? timeoutMs : 4000;
    return new Promise(function (resolve) {
      var settled = false;
      function finish(enabled) {
        if (settled) return;
        settled = true;
        resolve(!!enabled);
      }

      function requestFreshFlags() {
        applyMilestoneUserForFlags();

        var reloadRequested = false;
        onFeatureFlags(function () {
          if (!reloadRequested) return;
          // Only act on an explicit multivariate variant. Unmatched `false` is NOT control.
          var variant = readMilestoneVariantFromFlag();
          if (!variant) return;
          var enabled = variant === 'test';
          if (typeof onResolved === 'function') onResolved(enabled);
          finish(enabled);
        });

        reloadRequested = true;
        reloadFeatureFlags();
      }

      if (isSdkReady()) {
        requestFreshFlags();
      } else {
        window.addEventListener('whisky-posthog-ready', requestFreshFlags, { once: true });
      }

      window.setTimeout(function () {
        // Timeout: use live flag if known; do not treat unmatched false as durable control.
        finish(is3MonthMilestoneEnabled());
      }, wait);
    });
  }

  /** Cached hero variant decision so a visitor never flips between reloads. */
  var MILESTONE_VARIANT_STORAGE_KEY = 'qh_milestone_variant';

  function getCachedMilestoneVariant() {
    try {
      var raw = window.localStorage.getItem(MILESTONE_VARIANT_STORAGE_KEY);
      if (raw === 'test' || raw === 'control') return raw;
    } catch (e) {
      // ignore
    }
    return null;
  }

  function setCachedMilestoneVariant(variant) {
    if (variant !== 'test' && variant !== 'control') return;
    try {
      window.localStorage.setItem(MILESTONE_VARIANT_STORAGE_KEY, variant);
    } catch (e) {
      // ignore
    }
    // Notify Klaviyo form gate (and any other listeners) that assignment is durable.
    // TODO(cleanup): Remove this event when klaviyo-milestone-form-gate is deleted
    // after the 3-month-milestone experiment ends.
    try {
      window.dispatchEvent(
        new CustomEvent('qh-milestone-variant-ready', { detail: { variant: variant } })
      );
    } catch (e2) {
      // ignore
    }
  }

  /**
   * Resolve whether this visitor should see the 3-month-milestone hero.
   *
   * Priority:
   * 1) Live PostHog flag already `test` — always wins (upgrades a wrong sticky control cache)
   * 2) Durable `qh_milestone_variant` cache
   * 3) Existing quiz_sessionId with no live test → control (returning quiz-taker)
   * 4) Otherwise ask PostHog; only cache explicit `test` / `control` variants
   * @returns {Promise<boolean>}
   */
  function resolveMilestoneHeroVariant() {
    // Live test always wins — fixes flash→revert and sticky wrong control caches.
    if (is3MonthMilestoneEnabled()) {
      setCachedMilestoneVariant('test');
      return Promise.resolve(true);
    }

    var cached = getCachedMilestoneVariant();
    if (cached) return Promise.resolve(cached === 'test');

    if (hasQuizSessionId()) {
      return Promise.resolve(false);
    }

    return waitFor3MonthMilestoneFlag(undefined, function (enabled) {
      var variant = readMilestoneVariantFromFlag();
      if (variant) setCachedMilestoneVariant(variant);
      else if (enabled) setCachedMilestoneVariant('test');
      // Do not cache control from unmatched `false` / timeout.
    });
  }

  /**
   * Quiz-landing Golden Bear headline experiment (`homepage-headline-golden-bear`).
   * Flag key kept for continuity; surface is quiz-landing hero, not homepage.
   * Cached once so a visitor never flips control ↔ test across reloads (stable ads env).
   */
  var FEATURE_FLAG_QUIZ_LANDING_HEADLINE = 'homepage-headline-golden-bear';
  var QUIZ_LANDING_HEADLINE_VARIANT_STORAGE_KEY = 'qh_headline_variant';

  function getCachedQuizLandingHeadlineVariant() {
    try {
      var raw = window.localStorage.getItem(QUIZ_LANDING_HEADLINE_VARIANT_STORAGE_KEY);
      if (raw === 'test' || raw === 'control') return raw;
    } catch (e) {
      // ignore
    }
    return null;
  }

  function setCachedQuizLandingHeadlineVariant(variant) {
    if (variant !== 'test' && variant !== 'control') return;
    try {
      window.localStorage.setItem(QUIZ_LANDING_HEADLINE_VARIANT_STORAGE_KEY, variant);
    } catch (e) {
      // ignore
    }
  }

  function isQuizLandingHeadlineFlagResolved() {
    var flag = getFeatureFlag(FEATURE_FLAG_QUIZ_LANDING_HEADLINE);
    return flag !== undefined && flag !== null;
  }

  function readQuizLandingHeadlineVariantFromFlag() {
    var flag = getFeatureFlag(FEATURE_FLAG_QUIZ_LANDING_HEADLINE);
    if (flag === 'test' || flag === true) return 'test';
    if (flag === 'control' || flag === false) return 'control';
    return null;
  }

  /**
   * Resolve quiz-landing headline variant once and durably.
   * Cached localStorage value wins; otherwise wait for PostHog flags, cache, register, capture.
   * @param {number} [timeoutMs]
   * @returns {Promise<'control'|'test'>}
   */
  function resolveQuizLandingHeadlineVariant(timeoutMs) {
    var cached = getCachedQuizLandingHeadlineVariant();
    if (cached) {
      register({ quiz_landing_headline_variant: cached });
      return Promise.resolve(cached);
    }

    var wait = typeof timeoutMs === 'number' ? timeoutMs : 4000;
    return new Promise(function (resolve) {
      var settled = false;

      function finish(variant) {
        if (settled) return;
        settled = true;
        var finalVariant = variant === 'test' ? 'test' : 'control';
        setCachedQuizLandingHeadlineVariant(finalVariant);
        register({ quiz_landing_headline_variant: finalVariant });
        capture('quiz_landing_headline_flag_evaluated', funnelContext({
          variant: finalVariant,
          flag_key: FEATURE_FLAG_QUIZ_LANDING_HEADLINE
        }));
        resolve(finalVariant);
      }

      function tryResolveFromFlag() {
        if (!isQuizLandingHeadlineFlagResolved()) return false;
        var fromFlag = readQuizLandingHeadlineVariantFromFlag();
        if (!fromFlag) return false;
        finish(fromFlag);
        return true;
      }

      function requestFlags() {
        onFeatureFlags(function () {
          tryResolveFromFlag();
        });
        if (tryResolveFromFlag()) return;
        reloadFeatureFlags();
      }

      if (isSdkReady()) {
        requestFlags();
      } else {
        window.addEventListener('whisky-posthog-ready', requestFlags, { once: true });
      }

      window.setTimeout(function () {
        var fromFlag = readQuizLandingHeadlineVariantFromFlag();
        finish(fromFlag || 'control');
      }, wait);
    });
  }

  function getQuizSessionId() {
    try {
      return (window.localStorage.getItem('quiz_sessionId') || '').trim();
    } catch (e) {
      return '';
    }
  }

  function getQuizNavSource() {
    try {
      return sessionStorage.getItem('quiz_nav_source') || '';
    } catch (e) {
      return '';
    }
  }

  function getTasteProfileResult() {
    var root = document.querySelector('.taste-profile-page');
    if (!root) return '';
    return (root.getAttribute('data-taste-profile') || '').trim();
  }

  function funnelContext(extra) {
    var ctx = {
      quiz_session_id: getQuizSessionId(),
      quiz_nav_source: getQuizNavSource(),
      taste_profile_result: getTasteProfileResult(),
      shopify_template: window._template
        ? [window._template.directory, window._template.name, window._template.suffix].filter(Boolean).join('.')
        : undefined
    };
    // Starter tier test (snippets/starter-tier-test.liquid): taste profile views only.
    var starterTest = window.__STARTER_TIER_TEST__;
    if (starterTest && starterTest.arm) {
      ctx.starter_tier_test_arm = starterTest.arm;
      ctx.starter_tier_test_source = starterTest.source;
    }
    if (extra && typeof extra === 'object') {
      Object.keys(extra).forEach(function (key) {
        ctx[key] = extra[key];
      });
    }
    return ctx;
  }

  window.whiskyPosthog = {
    capture: capture,
    register: register,
    registerOnce: registerOnce,
    identify: identify,
    setPersonProperties: setPersonProperties,
    setPersonPropertiesForFlags: setPersonPropertiesForFlags,
    setMilestoneUserOnce: setMilestoneUserOnce,
    applyMilestoneUserForFlags: applyMilestoneUserForFlags,
    getStoredMilestoneUser: getStoredMilestoneUser,
    setFirstSeenAtOnce: setFirstSeenAtOnce,
    applyFirstSeenAtForFlags: applyFirstSeenAtForFlags,
    isFeatureEnabled: isFeatureEnabled,
    getFeatureFlag: getFeatureFlag,
    onFeatureFlags: onFeatureFlags,
    reloadFeatureFlags: reloadFeatureFlags,
    is3MonthMilestoneEnabled: is3MonthMilestoneEnabled,
    waitFor3MonthMilestoneFlag: waitFor3MonthMilestoneFlag,
    resolveMilestoneHeroVariant: resolveMilestoneHeroVariant,
    getCachedMilestoneVariant: getCachedMilestoneVariant,
    setCachedMilestoneVariant: setCachedMilestoneVariant,
    resolveQuizLandingHeadlineVariant: resolveQuizLandingHeadlineVariant,
    getCachedQuizLandingHeadlineVariant: getCachedQuizLandingHeadlineVariant,
    funnelContext: funnelContext,
    getQuizSessionId: getQuizSessionId,
    getQuizNavSource: getQuizNavSource,
    getTasteProfileResult: getTasteProfileResult,
    flush: flush,
    markSdkReady: markSdkReady,
    isSdkReady: isSdkReady,
    isDebug: isDebug
  };

  function tryMarkReady() {
    if (isSdkReady()) {
      markSdkReady();
      return true;
    }
    return false;
  }

  var attempts = 0;
  var timer = setInterval(function () {
    attempts += 1;
    if (tryMarkReady() || attempts >= 150) clearInterval(timer);
  }, 100);

  document.addEventListener('DOMContentLoaded', tryMarkReady);
  window.addEventListener('whisky-posthog-ready', markSdkReady);
})(window);
